<!--
  The flashcard drill.

  Two directions, both multiple-choice so nothing has to be typed and the whole thing works on
  a phone:

  * **glyph -> transliteration** (recognition): a letter form is shown, you pick its
    transliteration.
  * **transliteration -> glyph** (recall): a transliteration is shown, you pick the form.

  Reveal-then-grade: you choose, the answer is shown right or wrong, and the letter is folded
  into a Leitner schedule that brings the weak ones back first (lib/alphabet.ts). Progress is
  per-letter and lives in localStorage (lib/stores/alphabetProgress.ts) — private, never sent
  to the server, so a viewer account can use it exactly like anyone else.

  The distractors are the visually nearest glyphs from the generated similarity list, so the
  wrong answers are plausible rather than random. Only letters that have a reference form are
  drilled, and the panel states the count — both read from the generated artifact rather than
  hardcoded, because the deck grew from 24 to 28 when ḏ/ġ/k/ẓ were recovered from the web
  archive and a hardcoded sentence would have gone quietly stale.

  Keyboard shortcuts are local to this panel and documented inline — they are deliberately NOT
  in lib/keymap.ts, which is the annotation workspace's map and whose help overlay would
  otherwise advertise keys that do nothing on an image.
-->
<script lang="ts">
  import {
    DRILLABLE,
    type DrillCard,
    type DrillDirection,
    type LetterStat,
    grade,
    makeCard,
    masteredCount,
    pickNext,
  } from '../lib/alphabet';
  import { ALPHABET_SOURCE } from '../lib/alphabet.generated';
  import { clearProgress, loadProgress, saveProgress } from '../lib/stores/alphabetProgress';

  const missing: readonly string[] = ALPHABET_SOURCE.missingLetters;

  let direction = $state<DrillDirection>('glyphToLetter');
  let stats = $state(loadProgress());
  let card = $state<DrillCard | null>(null);
  let picked = $state<number | null>(null);
  let lastId = $state<number | null>(null);

  const answered = $derived(picked !== null);
  const correct = $derived(answered && card !== null && picked === card.answerId);
  const mastered = $derived(masteredCount((id) => stats[id]));
  const seen = $derived(Object.keys(stats).length);

  function nextCard(): void {
    const prompt = pickNext(DRILLABLE, (id) => stats[id], Math.random, lastId ?? undefined);
    if (!prompt) return;
    card = makeCard(prompt, direction);
    picked = null;
    lastId = prompt.id;
  }

  function choose(id: number): void {
    if (answered || card === null) return;
    picked = id;
    const isRight = id === card.answerId;
    const now = new Date().toISOString();
    const updated: LetterStat = grade(stats[card.answerId], isRight, now);
    stats = { ...stats, [card.answerId]: updated };
    saveProgress(stats);
  }

  function setDirection(next: DrillDirection): void {
    direction = next;
    nextCard();
  }

  function reset(): void {
    clearProgress();
    stats = {};
    lastId = null;
    nextCard();
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.target instanceof HTMLElement && event.target.tagName === 'INPUT') return;
    if (!answered && card) {
      const index = Number(event.key) - 1;
      if (Number.isInteger(index) && index >= 0 && index < card.options.length) {
        event.preventDefault();
        choose(card.options[index]!.id);
        return;
      }
    }
    if (answered && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      nextCard();
    }
  }

  // Start with a card, and keep one whenever the direction changes leaves us empty.
  $effect(() => {
    if (card === null) nextCard();
  });
</script>

<svelte:window onkeydown={onKeydown} />

<section class="panel col" aria-labelledby="drill-heading" data-section="drill">
  <div class="row wrap head">
    <h2 id="drill-heading">Flashcards</h2>
    <span class="spacer"></span>
    <div class="row small progress" aria-live="polite">
      <span title="Letters at the top of the Leitner ladder">{mastered} / {DRILLABLE.length} mastered</span>
      <span class="muted">·</span>
      <span class="muted">{seen} seen</span>
    </div>
  </div>

  <div class="row wrap controls">
    <div class="row small directions" role="group" aria-label="Drill direction">
      <button
        type="button"
        class:primary={direction === 'glyphToLetter'}
        aria-pressed={direction === 'glyphToLetter'}
        onclick={() => setDirection('glyphToLetter')}
      >
        Form → transliteration
      </button>
      <button
        type="button"
        class:primary={direction === 'letterToGlyph'}
        aria-pressed={direction === 'letterToGlyph'}
        onclick={() => setDirection('letterToGlyph')}
      >
        Transliteration → form
      </button>
    </div>
    <span class="spacer"></span>
    <button type="button" class="subtle small" onclick={reset}>Reset progress</button>
  </div>

  <p class="small muted note">
    {#if missing.length === 0}
      All {ALPHABET_SOURCE.attestedLetters} attested letters are drilled.
    {:else}
      {ALPHABET_SOURCE.coveredLetters} of the {ALPHABET_SOURCE.attestedLetters} attested letters
      are drilled — the {missing.length} with no reference form
      (<span class="glyph">{missing.join(' ')}</span>) are left out rather than faked.
    {/if}
    Wrong answers come back soonest. Keys: <kbd>1</kbd>–<kbd>4</kbd> to answer,
    <kbd>Enter</kbd> for the next card.
  </p>

  {#if card}
    <div class="card col" data-direction={card.direction}>
      <div class="prompt" data-testid="prompt">
        {#if card.direction === 'glyphToLetter'}
          <img
            src={card.prompt.glyph ?? ''}
            alt="Hismaic letter to identify"
            width="66"
            height="88"
            class="prompt-glyph"
          />
          <span class="small muted">Which transliteration is this?</span>
        {:else}
          <span class="glyph prompt-translit">{card.prompt.display}</span>
          <span class="small muted">Which form is this?</span>
        {/if}
      </div>

      <ul class="options" role="list">
        {#each card.options as option, index (option.id)}
          {@const isAnswer = option.id === card.answerId}
          <li>
            <button
              type="button"
              class="option"
              class:right={answered && isAnswer}
              class:wrong={answered && picked === option.id && !isAnswer}
              disabled={answered}
              data-testid="option"
              data-correct={isAnswer}
              onclick={() => choose(option.id)}
            >
              <span class="key mono">{index + 1}</span>
              {#if card.direction === 'glyphToLetter'}
                <span class="glyph opt-translit">{option.display}</span>
              {:else}
                <img src={option.glyph ?? ''} alt={`Option ${index + 1}`} width="30" height="40" />
              {/if}
            </button>
          </li>
        {/each}
      </ul>

      {#if answered}
        <div class="row wrap verdict" class:ok={correct} class:bad={!correct} data-testid="verdict">
          <strong>{correct ? 'Correct' : 'Not quite'}</strong>
          <span class="small">
            This is <span class="glyph">{card.prompt.display}</span>
            <span class="muted mono">(class {card.prompt.id})</span>.
          </span>
          <span class="spacer"></span>
          <button type="button" class="primary" onclick={nextCard} data-testid="next">
            Next card
          </button>
        </div>
      {/if}
    </div>
  {/if}
</section>

<style>
  .head,
  .controls {
    gap: 8px;
  }

  .note {
    margin: 0;
    max-width: 82ch;
  }

  .directions button.primary {
    background: var(--accent-dim);
    border-color: var(--accent);
  }

  .card {
    gap: 12px;
    align-items: center;
  }

  .prompt {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 12px;
  }

  .prompt-glyph {
    background: var(--glyph-paper);
    border: 1px solid var(--line);
    border-radius: 4px;
    padding: 4px;
  }

  .prompt-translit {
    font-size: 2.4rem;
    line-height: 1;
  }

  .options {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(90px, 120px));
    gap: 8px;
    justify-content: center;
  }

  .option {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px;
    min-height: 58px;
  }

  .option .opt-translit {
    font-size: 1.4rem;
  }

  .option img {
    background: var(--glyph-paper);
    border-radius: 2px;
  }

  .option .key {
    font-size: var(--text-xs);
    color: var(--text-dim);
  }

  .option.right {
    border-color: var(--ok);
    background: color-mix(in srgb, var(--ok) 18%, transparent);
  }

  .option.wrong {
    border-color: var(--danger);
    background: color-mix(in srgb, var(--danger) 18%, transparent);
  }

  .verdict {
    width: 100%;
    gap: 8px;
    align-items: center;
    border-top: 1px solid var(--line);
    padding-top: 10px;
  }

  .verdict.ok strong {
    color: var(--ok);
  }

  .verdict.bad strong {
    color: var(--warn);
  }
</style>
